import { cConst, cRange, defaultLayer, gradient, vConst, vCurveRandom, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Reference effect #5: level-up fountain (plan §"Reference effects").
 * Looping: toon-ramp glow column + spiraling ribbon trails (vortex module +
 * trail renderer) + rising flicker sparkles.
 */
export const levelUpFountainEffect = {
    version: 1,
    meta: { name: 'Level-Up Fountain', duration: 2, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'lvl_column',
            name: 'glow column',
            delay: 0,
            duration: 0.1,
            maxParticles: 3,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(2), // one per loop cycle, alive the whole cycle
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.4),
                    rotation: vConst(0),
                    color: cConst([0.55, 1, 0.75, 0.95]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'vertical' } },
            modules: [{ type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') }],
            material: {
                blend: 'additive',
                sprite: { type: 'builtin', id: 'glow' },
                // Rising energy done right: scroll the SEAMLESS noise texture and mask
                // it with the static glow shape. (Scrolling the glow sprite itself just
                // slides a circle through the quad — it isn't tileable.) The stepped
                // gradient keeps the toon-ramp banding, riding the graph's nAge input.
                graph: {
                    nodes: [
                        { id: 'age', type: 'nAge', x: 0, y: 0 },
                        {
                            id: 'grad',
                            type: 'gradientSample',
                            x: 180,
                            y: 0,
                            params: {
                                // hot stops compensate for the noise multiply (~0.5 average)
                                gradient: gradient('stepped', [0, [1.8, 1.8, 1.8, 1]], [0.35, [1.5, 1.8, 1.55, 0.85]], [0.7, [1, 1.7, 1.3, 0.6]], [1, [0.55, 1.4, 1, 0]]),
                            },
                        },
                        { id: 'uv0', type: 'uv', x: 0, y: 160 },
                        { id: 'scroll', type: 'uvScroll', x: 160, y: 160, params: { x: 0, y: -0.35 } },
                        { id: 'noise', type: 'textureSample', x: 330, y: 160, params: { sprite: { type: 'builtin', id: 'noise' } } },
                        { id: 'mask', type: 'textureSample', x: 330, y: 300, params: { sprite: { type: 'builtin', id: 'glow' } } },
                        { id: 'shape', type: 'multiply', x: 520, y: 220 },
                        { id: 'tinted', type: 'multiply', x: 690, y: 110 },
                        { id: 'out', type: 'output', x: 860, y: 110 },
                    ],
                    edges: [
                        { id: 'e1', from: 'age', fromPort: 'out', to: 'grad', toPort: 't' },
                        { id: 'e2', from: 'uv0', fromPort: 'out', to: 'scroll', toPort: 'uv' },
                        { id: 'e3', from: 'scroll', fromPort: 'out', to: 'noise', toPort: 'uv' },
                        { id: 'e4', from: 'noise', fromPort: 'out', to: 'shape', toPort: 'a' },
                        { id: 'e5', from: 'mask', fromPort: 'out', to: 'shape', toPort: 'b' },
                        { id: 'e6', from: 'grad', fromPort: 'out', to: 'tinted', toPort: 'a' },
                        { id: 'e7', from: 'shape', fromPort: 'out', to: 'tinted', toPort: 'b' },
                        { id: 'e8', from: 'tinted', fromPort: 'out', to: 'out', toPort: 'color' },
                    ],
                },
            },
        }),
        defaultLayer({
            id: 'lvl_trails',
            name: 'spiral ribbons',
            delay: 0,
            duration: 2,
            maxParticles: 10,
            emitter: {
                mode: { type: 'rate', perSecond: 3.5 },
                shape: { type: 'ring', radius: 0.55 },
                initial: {
                    lifetime: vRange(1.1, 1.5),
                    speed: vRange(0.4, 0.7),
                    spread: vConst(5),
                    size: vConst(1),
                    rotation: vConst(0),
                    color: cRange([0.65, 1, 0.8, 1], [1, 0.95, 0.55, 1]),
                },
            },
            renderer: {
                type: 'trail',
                points: 22,
                width: {
                    preset: 'custom',
                    points: [
                        { t: 0, v: 1, outTan: 0 },
                        { t: 1, v: 0.05, inTan: -1.5 },
                    ],
                },
                widthScale: 0.16,
            },
            modules: [
                // constant upward pull + swirl = helix
                { type: 'gravity', enabled: true, strength: -2.6 },
                { type: 'vortex', enabled: true, strength: 5.5 },
                { type: 'drag', enabled: true, coefficient: 0.6 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 1, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
        defaultLayer({
            id: 'lvl_sparkles',
            name: 'rising sparkles',
            delay: 0,
            duration: 2,
            maxParticles: 48,
            emitter: {
                mode: { type: 'rate', perSecond: 18 },
                shape: { type: 'cone', angle: 10, radius: 0.7 },
                initial: {
                    lifetime: vRange(0.7, 1.2),
                    speed: vCurveRandom(curvePreset('fadeInOut'), 1.2, 2.4),
                    spread: vConst(6),
                    size: vRange(0.07, 0.16),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-120, 120),
                    color: cRange([0.8, 1, 0.85, 1], [1, 1, 0.6, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('flicker') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.12, [1, 1, 1, 1]], [1, [0.7, 1, 0.8, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
    ],
};
